import React from "react";
import {
  Button,
  Input,
  InputNumber,
  Segmented,
  Slider,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  Block,
  Corners,
  EmailStyles,
  BlockVariant,
  VARIANTS_FOR,
  DEFAULT_VARIANT,
  BUTTON_RADIUS,
  BUTTON_SIZES,
  SOCIAL_SIZES,
  DEFAULT_SOCIAL_SIZE,
  FILL_COLOURS,
  FillColour,
  INK_CHOICES,
  GROUND_INK,
  Ground,
  GROUND_LINE,
  groundOf,
  coloursForGround,
  BLOCK_ALIGNS,
  BlockAlign,
  IMAGE_FITS,
  IMAGE_RATIOS,
  ImageFit,
  ImageRatio,
  InkColour,
  LINE_CHOICES,
  LineColour,
  SocialLink,
  evenCorners,
  sectionTypeKey,
  DEFAULT_IMAGETEXT_RATIO,
  SPACER_DEFAULT,
} from "../../blocks";

import PanelTitle from "../PanelTitle";

import styles from "./index.module.scss";
import { SPACER_MIN } from "../LetterBlock";
import RichText from "../../../../Common/RichText";
import SocialRows from "./SocialRows";
import ImageField from "./ImageField";
import {
  BackgroundField,
  ColourField,
  CornersField,
  LinkField,
  SpacingField,
} from "../Fields";

interface BlockEditorProps {
  block: Block | undefined;
  onChange: (patch: Partial<Block>) => void;
  onRemove: () => void;
  /** False on the last section: a letter always keeps one. */
  canRemove: boolean;
  /** What a section falls back to: padding and corners are the letter's until
   * the section is given its own. */
  letterStyles: EmailStyles;
}

/** The ground a button's fill makes for the label on it. */
const BUTTON_GROUND: Record<FillColour, Ground> = {
  brand: "brand",
  accent: "accent",
  // All three light ones make the same ground for a label: the page colour is
  // a tint like the sheet's quiet one, not a dark to be read against.
  emailBackground: "light",
  subtleBackground: "light",
  background: "light",
};

/** The sections whose text opens as a heading rather than a paragraph. */
const HEADING_TYPES = ["header", "title", "logo"];

/**
 * What the one number on a section means, how far it may go, and what it reads
 * as before it is set. The last of those is the letter's own fallback, not the
 * minimum: a spacer a template left unsaid stands 24 tall on the sheet, and the
 * field beside it has to say 24.
 */
const SIZE: Record<
  string,
  { label: string; min: number; max: number; fallback: number }
> = {
  divider: { label: "thickness", min: 1, max: 8, fallback: 1 },
  spacer: {
    label: "height",
    min: SPACER_MIN,
    max: 120,
    fallback: SPACER_DEFAULT,
  },
};

/**
 * A run of settings, told apart by the rule above it and named when the name
 * adds something. The section's own settings carry no heading: the fields say
 * what they are, and a title over them only repeated what the panel already
 * says.
 */
function Group({
  title,
  children,
}: React.PropsWithChildren<{ title?: string }>) {
  return (
    // The rule belongs to a named group, where it marks the start of something
    // called something. Without a name there is nothing for it to mark, and the
    // panel's own spacing is the divide.
    <section className={title ? styles.group : styles.plainGroup}>
      {/* antd's own quiet text at its own size, with nothing over it. It used
          to be 12px uppercase with tracking — three decisions, none of them the
          library's, to say what a secondary Text already says. */}
      {title ? (
        <Typography.Text type="secondary">{title}</Typography.Text>
      ) : null}
      {children}
    </section>
  );
}

export default function BlockEditor({
  block,
  onChange,
  onRemove,
  canRemove,
  letterStyles,
}: BlockEditorProps) {
  const { t } = useTranslation();

  if (!block) {
    return (
      // Centred by the box around it, not by the Text itself: antd sets
      // `display: inline` on .ant-typography with the same weight as a module
      // class and injects it later, so `display: block` there lost the tie and
      // text-align had nothing to align. The size is antd's own default — the
      // Text carries no size of its own.
      <div className={styles.empty}>
        <Typography.Text type="secondary">
          {t("pages.campaigns.builder.nothingSelected")}
        </Typography.Text>
      </div>
    );
  }

  const hasImage = block.type === "image" || block.type === "imagetext";
  const patchSocials = (socials: SocialLink[]) => onChange({ socials });
  // A button carries a label, which is a plain field of its own; everything
  // else with words in it gets the editor.
  const hasText = !["image", "divider", "spacer", "social", "button"].includes(
    block.type,
  );
  // Sections that are one measurement: a rule's thickness, a gap's height, the
  // side of a social button.
  const sized = ["divider", "spacer", "social"].includes(block.type);
  // Which colours to offer: the ones that go with the ground this block sits
  // on. A button makes its own ground out of its fill; a colour picked by hand
  // has no pairing, so everything is offered.
  //
  // The same ground decides what a field shows as chosen when the block has not
  // chosen anything: without that, moving a block onto the accent left every
  // colour field pointing at a swatch that is no longer on offer, and nothing
  // looked selected.
  // A button is filled, and so is a social row drawn that way. What it is filled
  // with is the ground its label stands on — a white button on the accent still
  // takes dark words — so the fill decides its colours, not the block behind it.
  const variant: BlockVariant =
    block.variant ?? DEFAULT_VARIANT[block.type] ?? "fill";
  const filled =
    (block.type === "button" || block.type === "social") && variant === "fill";
  const fill: FillColour = block.fill ?? "brand";

  const ground: Ground = filled
    ? block.fillCustom
      ? "custom"
      : BUTTON_GROUND[fill]
    : groundOf(block);

  // What a colour field shows as chosen when the block has chosen nothing:
  // without it, moving a block onto another ground left every field pointing at
  // a swatch that is no longer on offer, and nothing looked selected.
  const groundInk: InkColour = GROUND_INK[ground] ?? GROUND_INK.light;

  const itemCorners: Corners =
    block.type === "social"
      ? evenCorners((block.size ?? DEFAULT_SOCIAL_SIZE) / 2)
      : block.type === "button"
        ? evenCorners(BUTTON_RADIUS)
        : letterStyles.imageRadius;

  // Filled, outlined, or the mark on its own — the same three for a button and
  // for a row of icons, because it is the same decision. Written once and
  // placed twice: each block type reads it in its own run of fields, so neither
  // has to take the other's order.
  const variantField = (
    <label className={styles.field}>
      {t("pages.campaigns.builder.blockVariant")}
      <Segmented<BlockVariant>
        block
        value={variant}
        onChange={(next) => {
          // Changing the shape changes the ground the words stand on: a filled
          // block stands them on its fill, an outlined one on the block behind
          // it. A colour chosen for the old ground is the wrong half of a pair
          // on the new one, so it is dropped and the pairing takes over — the
          // same guard the fill already had. A colour picked by hand is the
          // user's and stays.
          const next_ground: Ground =
            next === "fill"
              ? block.fillCustom
                ? "custom"
                : BUTTON_GROUND[fill]
              : groundOf(block);

          onChange({
            variant: next,
            ...(block.inkCustom ||
            !block.ink ||
            INK_CHOICES[next_ground].includes(block.ink)
              ? {}
              : { ink: undefined }),
          });
        }}
        options={(VARIANTS_FOR[block.type] ?? []).map((value) => ({
          value,
          label: t(`pages.campaigns.builder.variant_${value}`),
        }))}
      />
    </label>
  );

  return (
    <div className={styles.editor}>
      <PanelTitle>
        {t(`pages.campaigns.blockTypes.${sectionTypeKey(block.type)}`)}
      </PanelTitle>

      {/* The content first: writing it is the reason the panel is open, and
          everything below describes the section around it. */}
      {hasImage ? (
        <Group>
          {/* No label over it: the tile says what it is, and the group above
              already reads "Image". */}
          <ImageField
            value={block.image}
            onChange={(image) => onChange({ image })}
            ratio={
              block.type === "imagetext"
                ? (block.ratio ?? DEFAULT_IMAGETEXT_RATIO)
                : undefined
            }
            // The crop window and the panel's Ratio field are the same value.
            onRatioChange={(ratio) => onChange({ ratio })}
          />

          {block.type === "imagetext" ? (
            <label className={styles.field}>
              {t("pages.campaigns.builder.ratio")}
              <Segmented<ImageRatio>
                block
                value={block.ratio ?? DEFAULT_IMAGETEXT_RATIO}
                onChange={(ratio) => onChange({ ratio })}
                options={[...IMAGE_RATIOS]}
              />
            </label>
          ) : (
            <>
              <label className={styles.field}>
                {t("pages.campaigns.builder.imageSize")}
                <Segmented<ImageFit>
                  block
                  value={block.fit ?? "fill"}
                  onChange={(fit) => onChange({ fit })}
                  options={IMAGE_FITS.map((value) => ({
                    value,
                    label: t(`pages.campaigns.builder.fit_${value}`),
                  }))}
                />
              </label>

              {(block.fit ?? "fill") === "scale" ? (
                <div className={styles.field}>
                  {/* The label carries the value: antd's slider tooltip shows
                      it only while dragging, and this is a number worth reading
                      at rest. */}
                  <span className={styles.labelRow}>
                    {t("pages.campaigns.builder.width")}
                    <Typography.Text type="secondary">
                      {block.scale ?? 50}%
                    </Typography.Text>
                  </span>
                  <Slider
                    min={10}
                    max={100}
                    step={5}
                    value={block.scale ?? 50}
                    tooltip={{ formatter: (value) => `${value}%` }}
                    onChange={(scale) => onChange({ scale })}
                  />
                </div>
              ) : null}

              {/* Only worth asking when the picture is narrower than its block:
                  at Fill it has nowhere to go. */}
              {(block.fit ?? "fill") !== "fill" ? (
                <label className={styles.field}>
                  {t("pages.campaigns.builder.imageAlign")}
                  <Segmented<BlockAlign>
                    block
                    value={block.imageAlign ?? "center"}
                    onChange={(imageAlign) => onChange({ imageAlign })}
                    options={BLOCK_ALIGNS.map((value) => ({
                      value,
                      label: t(`pages.campaigns.builder.align_${value}`),
                    }))}
                  />
                </label>
              ) : null}
            </>
          )}

          {block.type === "imagetext" ? (
            <label className={styles.field}>
              {t("pages.campaigns.builder.imageSide")}
              <Segmented<"left" | "right">
                block
                value={block.imageSide ?? "left"}
                onChange={(imageSide) => onChange({ imageSide })}
                options={[
                  {
                    value: "left",
                    label: t("pages.campaigns.builder.alignStart"),
                  },
                  {
                    value: "right",
                    label: t("pages.campaigns.builder.alignEnd"),
                  },
                ]}
              />
            </label>
          ) : null}

          <label className={styles.field}>
            {t("pages.campaigns.builder.link")}
            <LinkField
              value={block.link}
              onChange={(link) => onChange({ link })}
            />
          </label>

          <label className={styles.field}>
            {t("pages.campaigns.builder.alt")}
            <Input
              value={block.alt}
              placeholder={t("pages.campaigns.builder.altHint")}
              onChange={(event) => onChange({ alt: event.target.value })}
            />
          </label>
        </Group>
      ) : null}

      {block.type === "button" ? (
        <label className={styles.field}>
          {t("pages.campaigns.builder.label")}
          <Input
            value={block.text}
            onChange={(event) => onChange({ text: event.target.value })}
          />
        </label>
      ) : null}

      {hasText ? (
        // Labelled only where the editor shares the panel with a picture: on a
        // text block it is the whole of what the panel is for and needs no
        // saying.
        <div className={styles.field}>
          {block.type === "imagetext"
            ? t("pages.campaigns.builder.text")
            : null}
          <RichText
            value={block.text}
            onChange={(text) => onChange({ text })}
            // Heading and Paragraph are the same section with a different style
            // chosen for them.
            defaultStyle={
              HEADING_TYPES.includes(block.type) ? "h1" : "paragraph"
            }
          />
        </div>
      ) : null}

      {block.type === "button" ? (
        <label className={styles.field}>
          {t("pages.campaigns.builder.link")}
          <LinkField
            value={block.link}
            onChange={(link) => onChange({ link })}
          />
        </label>
      ) : null}

      {block.type === "button" ? (
        <label className={styles.field}>
          {t("pages.campaigns.builder.buttonSize")}
          <Segmented<number>
            block
            value={block.size ?? BUTTON_SIZES[1].height}
            onChange={(size) => onChange({ size })}
            options={BUTTON_SIZES.map(({ height, label }) => ({
              value: height,
              label: t(`pages.campaigns.builder.${label}`),
            }))}
          />
        </label>
      ) : null}

      {block.type === "button" ? variantField : null}

      {block.type === "button" ? (
        <label className={styles.field}>
          {t("pages.campaigns.builder.buttonAlign")}
          <Segmented<BlockAlign>
            block
            value={block.align ?? "center"}
            onChange={(align) => onChange({ align })}
            options={BLOCK_ALIGNS.map((value) => ({
              value,
              label: t(`pages.campaigns.builder.align_${value}`),
            }))}
          />
        </label>
      ) : null}

      {block.type === "social" ? (
        <Group>
          <SocialRows value={block.socials ?? []} onChange={patchSocials} />

          <label className={styles.field}>
            {t("pages.campaigns.builder.buttonSize")}
            {/* Three sizes rather than a number: a social button is a target for
                a finger, and the useful range is narrow enough to name. */}
            <Segmented<number>
              block
              value={block.size ?? DEFAULT_SOCIAL_SIZE}
              onChange={(size) => onChange({ size })}
              options={SOCIAL_SIZES.map(({ value, label }) => ({
                value,
                label: t(`pages.campaigns.builder.${label}`),
              }))}
            />
          </label>

          {variantField}

          <div className={styles.field}>
            {t("pages.campaigns.builder.buttonCorners")}
            <CornersField
              value={block.itemRadius ?? itemCorners}
              onChange={(itemRadius) => onChange({ itemRadius })}
            />
          </div>
        </Group>
      ) : null}

      {sized && block.type !== "social" ? (
        <label className={styles.field}>
          {t(`pages.campaigns.builder.${SIZE[block.type].label}`)}
          <InputNumber
            min={SIZE[block.type].min}
            max={SIZE[block.type].max}
            value={block.size ?? SIZE[block.type].fallback}
            onChange={(size) =>
              onChange({ size: size ?? SIZE[block.type].fallback })
            }
          />
        </label>
      ) : null}

      {block.type === "divider" ? (
        <div className={styles.field}>
          {t("pages.campaigns.builder.dividerColor")}
          <ColourField<LineColour>
            value={block.line ?? GROUND_LINE[groundOf(block)]}
            custom={block.lineCustom}
            options={(LINE_CHOICES[ground] ?? LINE_CHOICES.light).map(
              (key) => ({
                key,
                colour: letterStyles[key],
                label: t(`pages.campaigns.swatches.${key}`),
              }),
            )}
            onChange={({ key, custom }) =>
              onChange({ line: key ?? block.line, lineCustom: custom })
            }
          />
        </div>
      ) : null}

      {/* What fills it — the letter's own button colour unless the section
          names another, or carries one of its own. Only where there is a fill
          to colour: an outlined button draws its edge in the ground's rule
          colour, which is the letter's to set. */}
      {filled ? (
        <div className={styles.field}>
          {t("pages.campaigns.builder.fillColor")}
          <ColourField<FillColour>
            value={block.fill ?? "brand"}
            custom={block.fillCustom}
            options={FILL_COLOURS.map((key) => ({
              key,
              colour: letterStyles[key],
              label: t(`pages.campaigns.swatches.${key}`),
            }))}
            onChange={({ key, custom }) => {
              const next = key ?? fill;

              onChange({
                fill: key ?? block.fill,
                fillCustom: custom,
                // The label was picked for the fill the button had. On another
                // one it is the wrong half of a pair — white on white, or the
                // sheet's dark on the brand. Where the new fill has no use for
                // it, it is dropped and the pairing takes over again; a colour
                // picked by hand is the user's and stays.
                ...(custom ||
                block.inkCustom ||
                !block.ink ||
                INK_CHOICES[BUTTON_GROUND[next]].includes(block.ink)
                  ? {}
                  : { ink: undefined }),
              });
            }}
          />
        </div>
      ) : null}

      {/* The colour the block's words or icons are set in. Only where there
          are any: a picture has none, a divider's own colour is asked for
          above with the rule itself, and a spacer draws nothing at all. */}
      {hasText || block.type === "button" || block.type === "social" ? (
        <div className={styles.field}>
          {t(
            block.type === "social"
              ? "pages.campaigns.builder.iconColor"
              : "pages.campaigns.builder.textColor",
          )}
          <ColourField<InkColour>
            value={block.ink ?? groundInk}
            custom={block.inkCustom}
            options={INK_CHOICES[ground].map((key) => ({
              key,
              colour: letterStyles[key],
              label: t(`pages.campaigns.swatches.${key}`),
            }))}
            onChange={({ key, custom }) =>
              onChange({ ink: key ?? block.ink, inkCustom: custom })
            }
          />
        </div>
      ) : null}

      {block.type === "button" ? (
        <div className={styles.field}>
          {t("pages.campaigns.builder.buttonCorners")}
          <CornersField
            value={block.itemRadius ?? itemCorners}
            onChange={(itemRadius) => onChange({ itemRadius })}
          />
        </div>
      ) : null}

      {/* Only where there is a picture to round: a spacer, a rule or a line
          of text has none, and a social row asks for its buttons' corners in
          its own group. */}
      {hasImage ? (
        <div className={styles.field}>
          {t("pages.campaigns.builder.imageCorners")}
          <CornersField
            value={block.itemRadius ?? itemCorners}
            onChange={(itemRadius) => onChange({ itemRadius })}
          />
        </div>
      ) : null}

      {/* The section around the content: its ground, its air and its corners.
          Each starts from the letter's own setting and can be answered here for
          this section alone. */}
      <Group title={t("pages.campaigns.builder.groupBlock")}>
        <div className={styles.field}>
          {t("pages.campaigns.builder.blockBackground")}
          <BackgroundField
            value={block.bg ?? "Default"}
            custom={block.bgCustom}
            colours={{
              Default: letterStyles.background,
              Soft: letterStyles.subtleBackground,
              Email: letterStyles.emailBackground,
              Accent: letterStyles.accent,
              Brand: letterStyles.brand,
            }}
            // The block's own colours move with its ground: a text colour
            // chosen for the sheet becomes the accent's when the block lands
            // there, and back again when it leaves.
            //
            // Except on a filled block. A button's label stands on the button,
            // not on the block behind it, so moving the block onto another
            // ground says nothing about what colour the label should be — that
            // follows the fill, and only the fill.
            onChange={(patch) =>
              onChange({
                ...patch,
                ...(filled ? {} : coloursForGround(block, groundOf(patch))),
              })
            }
          />
        </div>

        {/* A spacer keeps no space around its content: it has none, and its
            height is the whole of what it is. */}
        {block.type === "spacer" ? null : (
          <div className={styles.field}>
            {t("pages.campaigns.builder.blockPaddings")}
            <SpacingField
              value={block.padding ?? letterStyles.padding}
              onChange={(padding) => onChange({ padding })}
            />
          </div>
        )}

        {/* Every section, a rule and a gap included: the corners belong to the
            band the section draws, not to what is written in it. */}
        <div className={styles.field}>
          {t("pages.campaigns.builder.blockCorners")}
          <CornersField
            value={block.radius ?? letterStyles.radius}
            onChange={(radius) => onChange({ radius })}
          />
        </div>
      </Group>

      {/* Twice the panel's own rhythm above it: the one control here that
          cannot be undone should not sit a field's width from the last
          setting. */}
      <Button
        danger
        block
        className={styles.remove}
        disabled={!canRemove}
        onClick={onRemove}
      >
        {t("pages.campaigns.builder.remove")}
      </Button>
    </div>
  );
}
